import { useState, useEffect } from "react";
import EduvateLogoImage from "../../../assets/images/Eduvatelogo.svg";
import { useNavigate } from "react-router-dom";
import { signUp } from "../../api/signup";
import {commonSuccessToastMessage,signUpSuccessToast,commonErrorToastMessage} from "../../components/toastify/AllToastifyErrors";
import loginHowItWorks1Image from "../../../assets/images/LoginPage/loginHowItWorks1.png";
import loginHowItWorks2Image from "../../../assets/images/LoginPage/loginHowItWorks2.png";
import loginCopyPasteImage from "../../../assets/images/LoginPage/loginCopyPaste.png";
import loginChooseKeywordsImage from "../../../assets/images/LoginPage/loginChooseKeywords.png";
import loginGetQuestionsImage from "../../../assets/images/LoginPage/loginGetQuestions.png";
import SignUpForm from "./SignupForm";
import { validateFormFields } from "../../utils/Validations/signupValidation";
import { Tooltip, Zoom } from "@mui/material";
import LoadingOverlay from "../../components/overlays/LoadingOverlay";

const SignupPage = () => {
  const navigate = useNavigate();

  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInvalidInput, setHasInvalidInput] = useState(false);
  
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    occupation: 'other',
    otherOccupation: "",
    Version:'Free'
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    occupation: 'Other',
    otherOccupation: "",
    Version:'Free'
  });

  // Security validation function to detect links and scripts
  const containsLinkOrScript = (value) => {
    if (!value) return false;
    
    const linkPattern = /(https?:\/\/|www\.|ftp:\/\/|mailto:|<script|<\/script|javascript:|data:|onerror=|onclick=|<iframe|<embed|<object)/gi;
    return linkPattern.test(value);
  };

  // Validate password length (minimum 8 characters)
  const validatePasswordLength = (password) => {
    return password.length >= 8;
  };

  const checkPasswordStrength = (password) => {
    const strength = {
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };
    
    const score = Object.values(strength).filter(Boolean).length;
    
    return {
      ...strength,
      score,
      isStrong: score >= 4 && strength.hasMinLength,
      message: score === 5 ? "Strong password" :
               score >= 4 ? "Good password" :
               score >= 3 ? "Fair password" :
               "Weak password"
    };
  };

  // Check all fields for security issues
  useEffect(() => {
    const checkForInvalidInput = () => {
      // Check all text fields for links/scripts
      const hasLink = 
        containsLinkOrScript(userData.firstName) ||
        containsLinkOrScript(userData.lastName) ||
        containsLinkOrScript(userData.email) ||
        containsLinkOrScript(userData.otherOccupation);

      // Check password length
      const hasShortPassword = userData.password && !checkPasswordStrength(userData.password);

      setHasInvalidInput(hasLink || hasShortPassword);

      // Set error messages for fields with issues
      if (containsLinkOrScript(userData.firstName)) {
        setErrors(prev => ({ ...prev, firstName: "Links and scripts are not allowed" }));
      }
      if (containsLinkOrScript(userData.lastName)) {
        setErrors(prev => ({ ...prev, lastName: "Links and scripts are not allowed" }));
      }
      if (containsLinkOrScript(userData.email)) {
        setErrors(prev => ({ ...prev, email: "Links and scripts are not allowed" }));
      }
      if (containsLinkOrScript(userData.otherOccupation)) {
        setErrors(prev => ({ ...prev, otherOccupation: "Links and scripts are not allowed" }));
      }
      if (hasShortPassword) {
        setErrors(prev => ({ ...prev, password: "Password must be at least 8 characters" }));
      }
    };

    checkForInvalidInput();
  }, [userData]);

  const handleSignUp = async () => {
    try {
      // Check for security issues before proceeding
      if (hasInvalidInput) {
        commonErrorToastMessage('Please fix all errors before submitting');
        return;
      }

      // Validate password length
      if (!checkPasswordStrength(userData.password)) {
        setErrors(prev => ({ ...prev, password: "Password must be at least 8 characters" }));
        commonErrorToastMessage('Password must be at least 8 characters');
        return;
      }

      // Validate First Name and Last Name
      const validationErrors = validateFormFields(
        userData,
        setErrors,
      );

      if (errors)
        if (
          Object.values(errors).every((errorMessage) => errorMessage === "")
        ) {
          // If there are no errors, proceed with signup
          try {
            setErrors("");
          } catch (error) {
            console.log("Error while Sign up :", error);
          }
        }

      try {
        // sing up API calling
        if (!isAgreementChecked) {
          commonErrorToastMessage('Please click and accept the Terms & Condition.')
          return;
        }
        setIsLoading(true);
        const response = await signUp(
          userData.firstName,
          userData.lastName,
          userData.email,
          userData.password,
          userData.confirmPassword,
          userData.occupation,
          userData.Version
        );

      if (response.status === 200 || response.message === 'User registered successfully.'){
        commonSuccessToastMessage(
          "Bravo! You've successfully signed up. Great to have you with us!"
        );
        setIsLoading(false);
        navigate("/");
      }else{
        setIsLoading(false);
        commonErrorToastMessage(response.message);
        return;
      }
      } catch (error) {
        console.error("Error:", error.response?.status);
       if (error?.response?.status === 400) {
        const errorMessage = error?.response?.data?.message;
        
        // Check for email validation errors
        if (errorMessage === 'Email must contain @ sign.' || 
            errorMessage === 'Please enter a valid email address.') {
          setErrors((prevErrors) => ({
            ...prevErrors,
            email: errorMessage,
          }));
          setIsLoading(false);
          commonErrorToastMessage(errorMessage);
          return;
        }
        
        // Handle other 400 errors
        setIsLoading(false);
        commonErrorToastMessage(errorMessage || 'Invalid input data.');
        return;
      } 
      else if (error?.response?.status === 409) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          email: "Account with this email address already exists. Please try logging in instead.",
        }));
        setIsLoading(false);
        commonErrorToastMessage("Account with this email already exists.");
        return;
      } 
      else {
        setErrors((prevErrors) => ({ ...prevErrors, email: "" }));
        setIsLoading(false);
        commonErrorToastMessage(error?.response?.data?.message || "An error occurred during signup.");
        return;
      }
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error:", error);
      commonErrorToastMessage(error.message);
      return;
    }
  };

  const backtologin = async() => {
    navigate('/')
  }

  // Check if sign up button should be disabled
  const isSignUpDisabled = hasInvalidInput || !isAgreementChecked || isLoading;

  return (
    <div className=" flex flex-col min-h-screen">
            {/* Header section */}
            <div className="bg-[#042739] h-[13vh] px-10 py-3 flex justify-between items-center md:flex sm:grid ">
              <div className="flex items-center w-[18vh]">
                {/* Quillionz Logo */}
                <img src={EduvateLogoImage} onClick={backtologin} alt="Logo" className="cursor-pointer" />
              </div>
            </div>

      <div className="flex-grow">
        <div className="flex md:h-[87vh] custom-scrollbar overflow-x-hidden overflow-y-auto animate-fade-slide-down gap-16">
            <div className=" w-1/2 flex bg-[#1462b0] overflow-hidden">
                    {/* Left side images */}
                    <div className="w-1/2 ">
                      <div className="">
                        <img src={loginHowItWorks1Image} alt="Logo" className="" />
                      </div>
                      <div className="">
                        <img src={loginHowItWorks2Image} alt="Logo" className="" />
                      </div>
                    </div>
          
                    {/* Explanation text */}
                    <div className="w-1/2 text-white flex flex-col align-middle place-content-center justify-center items-center text-center">
                      {/* Steps with icons */}
                      <div className="flex flex-col items-center justify-center ">
                        <p>
                          <img src={loginCopyPasteImage} alt="Logo" className="" />
                        </p>
                        <p className="my-3 text-sm">1. Submit Content</p>
                      </div>
          
                      <div className="flex flex-col items-center justify-center ">
                        <p>
                          <img src={loginChooseKeywordsImage} alt="Logo" className="" />
                        </p>
                        <p className="my-3 text-sm">2. Question Selection</p>
                      </div>
          
                      <div className="flex flex-col items-center justify-center ">
                        <p>
                          <img src={loginGetQuestionsImage} alt="Logo" className="" />
                        </p>
                        <p className="my-3 text-sm">3. Get Question and Summary Ideas</p>
                      </div>
                    </div>
                  </div>
          <div className=" w-2/5 flex flex-col justify-center items-center animate-fade-slide-down ">
            
           <p className="text-5xl text-[#042739] font-semibold">EDWiN </p>
            {/* Signup Form Starts here */}
            <div className="mt-4">
              <SignUpForm
                userData={userData}
                setUserData={setUserData}
                errors={errors}
                setErrors={setErrors}
              />

              <p className="mb-3 cursor-pointer text-justify">
                <input
                  className="me-2"
                  type="checkbox"
                  name=""
                  id=""
                  checked={isAgreementChecked}
                  onChange={() => setIsAgreementChecked(!isAgreementChecked)}
                />
                <span
                  onClick={() => setIsAgreementChecked(!isAgreementChecked)}
                >
                  Yes, I agree that Eduvate may contact me for news, updates,
                  marketing and sales purposes. I confirm to the processing of
                  my Personal Data as per the terms in the{" "}
                </span>

                <span className="text-[#337ab7] transition ease-in-out delay-150 cursor-pointer hover:text-[#f15f02] ">
                  <Tooltip
                    title="Terms of use"
                    arrow
                    placement="top"
                    TransitionComponent={Zoom}
                    TransitionProps={{ timeout: 300 }}
                  >
                    
                    <a  href="/Eduvate_terms_conditions.html"
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#337ab7] transition ease-in-out delay-150 cursor-pointer hover:text-[#f15f02] underline"
      onClick={(e) => e.stopPropagation()}
    >
      Terms of Use
    </a>
                  </Tooltip>
                </span>
              </p>
              <p className="text-center">
                {errors.checkbox && (
                  <span className="text-red-500 antialiased animate-fade-slide-down text-[13px]">
                    {errors.checkbox}
                  </span>
                )}
              </p>
            </div>

            <div className="text-center">
              <Tooltip
                title={isSignUpDisabled ? "Please fix all errors and accept terms" : "Sign up"}
                arrow
                placement="top"
                TransitionComponent={Zoom}
                TransitionProps={{ timeout: 300 }}
              >
                <span>
                  <button
                    className={`text-lg font-bold border-2 border-[#f15f02] px-4 py-2 mt-7 mb-8 text-center transition ease-in-out delay-150 ${
                      isSignUpDisabled 
                        ? 'text-gray-400 bg-gray-200 cursor-not-allowed opacity-50' 
                        : 'text-[#112a38] hover:bg-[#EF7920] hover:text-white'
                    }`}
                    onClick={handleSignUp}
                    disabled={isSignUpDisabled}
                  >
                    Sign Up
                  </button>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
      {isLoading && <LoadingOverlay />}
    </div>
  );
};

export default SignupPage;